// The whole profile in one shape. Every section reads and writes a slice of it,
// and the page holds a saved copy plus a draft so an edit can be cancelled.
export interface CompanyProfileData {
  additionalContext: string
  companyName: string
  competitors: string[]
  otherPriority: string
  priorities: string[]
  // Lists, entered as chips like competitors — a customer names several
  // products and several audiences, not one line of each.
  products: string[]
  targetAudience: string[]
}

// Which single section is open for editing once the profile has been saved.
// null means nothing is being edited — every section shows its summary.
export type EditableSection =
  | 'additionalContext'
  | 'companyInformation'
  | 'competitors'
  | 'priorities'
  | 'products'
  | null

// Mirrors sm.back's caps (MAX_SHORT / MAX_CONTEXT). Enforced with maxLength so
// an over-long value cannot be entered: the error modal is a fixed design that
// cannot say which field is at fault, so "try again" on a validation failure
// would fail identically every time.
export const MAX_ENTRY_LENGTH = 200
export const MAX_CONTEXT_LENGTH = 4000

export const EMPTY_PROFILE: CompanyProfileData = {
  additionalContext: '',
  companyName: '',
  competitors: [],
  otherPriority: '',
  priorities: [],
  products: [],
  targetAudience: [],
}

export const PRIORITY_OPTIONS = [
  'Brand Health',
  'Customer Experience',
  'Pricing & Value Proposition',
  'Market & Consumer Insights',
  'Product & Service Feedback',
  'Crisis & Risk Management',
  'Brand Reputation',
  'Employer Brand',
] as const

// The checkbox rows read down each column, not across the row, so the design's
// left column is the even indices and the right column the odd ones.
export const PRIORITY_COLUMNS: [string[], string[]] = [
  [
    'Brand Health',
    'Pricing & Value Proposition',
    'Product & Service Feedback',
    'Brand Reputation',
  ],
  [
    'Customer Experience',
    'Market & Consumer Insights',
    'Crisis & Risk Management',
    'Employer Brand',
  ],
]

// Save turns on once every field the design marks "Required" is filled. The
// company name is deliberately not among them — it carries no Required tag in
// the design, so it must not gate the button.
export const isProfileComplete = (d: CompanyProfileData): boolean =>
  d.competitors.length > 0 &&
  (d.priorities.length > 0 || d.otherPriority.trim() !== '') &&
  d.products.length > 0 &&
  d.targetAudience.length > 0 &&
  d.additionalContext.trim() !== ''

// A document can exist and still hold nothing — a PUT with an empty body
// leaves one behind. Summarising blanks would be worse than asking again, so
// an empty profile counts as not filled in yet.
export const isProfileEmpty = (d: CompanyProfileData): boolean =>
  d.companyName.trim() === '' &&
  d.competitors.length === 0 &&
  d.priorities.length === 0 &&
  d.otherPriority.trim() === '' &&
  d.products.length === 0 &&
  d.targetAudience.length === 0 &&
  d.additionalContext.trim() === ''

// Whether the section currently open for editing satisfies the same Required
// rule the create form enforces. Without this a section could be emptied on
// edit and saved, reaching a state the create form would never have allowed.
export const isSectionComplete = (
  section: EditableSection,
  d: CompanyProfileData
): boolean => {
  switch (section) {
    // The company name carries no Required marker, so it may be cleared.
    case 'companyInformation':
      return true
    case 'competitors':
      return d.competitors.length > 0
    case 'priorities':
      return d.priorities.length > 0 || d.otherPriority.trim() !== ''
    case 'products':
      return d.products.length > 0 && d.targetAudience.length > 0
    case 'additionalContext':
      return d.additionalContext.trim() !== ''
    default:
      return true
  }
}

// The keys each editable section owns, so a PATCH carries only its own fields.
const SECTION_FIELDS: Record<
  Exclude<EditableSection, null>,
  (keyof CompanyProfileData)[]
> = {
  additionalContext: ['additionalContext'],
  companyInformation: ['companyName'],
  competitors: ['competitors'],
  priorities: ['otherPriority', 'priorities'],
  products: ['products', 'targetAudience'],
}

export const sectionPayload = (
  section: EditableSection,
  draft: CompanyProfileData
): Partial<CompanyProfileData> => {
  if (!section) return {}
  return SECTION_FIELDS[section].reduce<Partial<CompanyProfileData>>(
    (acc, key) => ({ ...acc, [key]: draft[key] }),
    {}
  )
}
